/**
 * Copyright (c) 2018 Honeywell International Inc. All Rights Reserved.
 * Honeywell and its product names are among the trademarks and/or service marks owned by Honeywell International, Inc., or its subsidiaries.
 *
 * For patent information, see http://www.hsmpats.com.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { AppContext } from '@keystone/common';
import APIClient from '../../common/APIClient';

export const ConfigurationContext = React.createContext();
export const ConfigurationConsumer = ConfigurationContext.Consumer;

class ConfigurationProvider extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      featureData: [],
      removedFeatureRows: [],
    };
  }

  fetchConfiguration = async () => {
    let configuration = null;
    const { user, handleError } = this.context;
    try {
      configuration = await APIClient.fetchConfiguration(user);
    } catch (err) {
      handleError(err);
    }
    return configuration.response;
  };

  saveConfiguration = async (emails) => {
    const { user, handleError, setNotification } = this.context;
    const { featureData, removedFeatureRows } = this.state;
    try {
      this.verifyFeatureData();
      await APIClient.saveConfiguration(emails, user);
      await APIClient.updatePermissions(featureData, user);
      await APIClient.deletePermissions(removedFeatureRows, user);
      setNotification(`Configuration saved`, 'success');
    } catch (err) {
      handleError(err);
      return false;
    }
    return true;
  };

  // FeatureAccessTable
  // fetch currently defined domain -> feature list rules
  fetchFeatureData = async () => {
    const { user } = this.context;
    const featureData = await APIClient.getAllPermissions(user);
    this.setState({ featureData: featureData.response });
  };

  verifyFeatureData = () => {
    const { featureData } = this.state;
    const domains = [];
    featureData.forEach((item) => {
      if (item.emailDomain.length === 0 || domains.includes(item.emailDomain)) {
        throw new Error('Empty or duplicate domain values given in Feature Access table');
      }
      domains.push(item.emailDomain);
    });
  };

  updateContextState = (key, value) => {
    this.setState({ [key]: value });
  };

  uploadSLA = async (data) => {
    const { user, setNotification, handleError } = this.context;
    try {
      const result = await APIClient.uploadSLA(data, user);
      if (result.response.result === 0) {
        setNotification('SLA uploaded successfully.', 'success');
      } else {
        setNotification('An error occured', 'critical');
      }
    } catch (err) {
      handleError(err);
    }
  };

  render() {
    const { children } = this.props;
    const { featureData, removedFeatureRows } = this.state;
    return (
      <ConfigurationContext.Provider
        value={{
          appContext: this.context,
          // api methods
          fetchConfiguration: this.fetchConfiguration,
          saveConfiguration: this.saveConfiguration,

          // FeatureAccessTable
          featureData,
          removedFeatureRows,
          fetchFeatureData: this.fetchFeatureData,
          updateContextState: this.updateContextState,
          uploadSLA: this.uploadSLA,
          resetResults: this.resetResults,
        }}
      >
        {children}
      </ConfigurationContext.Provider>
    );
  }
}

ConfigurationProvider.contextType = AppContext;

ConfigurationProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ConfigurationProvider;
